import type { Dispatch, SetStateAction } from 'react';
import React from 'react';
import { Redirect, Route, Switch, useLocation } from '@react-router';
import type { RoutePath, RoutesPaths } from '@shared/core';
import { RoutesProvider, useRouterHelper } from '@shared/core';
import { MustBeSignedIn } from '@shared/components/MustBeSignedIn/MustBeSignedIn';
import { PostOffice } from './PostOffice';
import { usePostOfficeController } from './PostOffice.controller';
import { usePostOfficeRouterController } from './PostOfficeRouter.controller';
import { Confirm, EmailEditor } from '@shared/components/EmailsAndEcards';
import type { PostOfficeMessageType } from '@graphql/generated';
import { JoyKitThemeProvider } from '@withjoy/joykit';
import { JoyKitV2AdminTheme } from '@shared/utils/style/joykitV2AdminTheme';
import { TelemetryProvider } from './PostOffice.telemetry';
import { PeopleProvider } from '@shared/context/people';
import { PostOfficeBottomSheetProvider, PostOfficeBottomSheet } from './PostOffice.bottomSheet';
import ConfirmSheet from './components/ConfirmSheet';
import MobileSendTestEmailDialog from './components/MobileSendTestEmailDialog';
import ConfirmSheetThankYou from './components/ConfirmSheetThankYou';
import { UnpublishedWebsiteWarning } from '@apps/admin/routes/WebsiteDesignerV2/components/UnpublishedWebsiteWarning/UnpublishedWebsiteWarning';
import { EventFeature, useProFeature } from '@shared/hooks/useProFeature';

/**
 * Example Route name types. Defines the routes that are available
 */
export type PostOfficeRouteNames = 'create' | 'confirm';

export type PostOfficeRoutePaths = RoutesPaths<PostOfficeRouteNames>;
/**
 * Example route paths. Has a path for the <Router /> and a function for history to push to.
 */
export const postOfficeRoutePaths: PostOfficeRoutePaths = {
  create: {
    path: `create`,
    goToPath: () => `create`,
  },
  confirm: {
    path: `confirm`,
    goToPath: () => `confirm`,
  },
};

const RoutesPostOffice: React.FC<{
  draftType: PostOfficeMessageType;
  eventDisplayName: string;
  eventHandle: string;
  eventId: string;
  setIsThankYouMessageLoading: Dispatch<SetStateAction<boolean>>;
}> = ({ draftType, eventDisplayName, eventHandle, eventId, setIsThankYouMessageLoading }) => {
  const routeHelpers = useRouterHelper();

  const {
    // postOfficeFormik: formik,
    handleSendEmail,
    testEmail,
    emailIsVerified,
    isPasswordToggleShown,
    footerText,
    eventPassword,
    loadingDraftData,
    emailInputState,
    updateEmailInput,
    pages,
    isMobile,
    hideGuestSelector,
    isMessageLoading,
    recipientName,
    unpublishedWebsiteWarningProps,
  } = usePostOfficeController({ eventId, draftType, eventDisplayName, eventHandle, setIsThankYouMessageLoading });
  const editor = (
    <EmailEditor
      eventPassword={eventPassword}
      loadingDraftData={loadingDraftData}
      isPasswordToggleShown={isPasswordToggleShown}
      emailType={draftType === 'thankyou' ? 'thankyou' : 'generic'}
      emailInput={emailInputState}
      updateEmailInput={updateEmailInput}
      footerText={footerText}
      eventHandle={eventHandle}
      eventDisplayName={eventDisplayName}
      pages={pages}
      isMessageLoading={isMessageLoading}
      recipientName={recipientName}
    />
  );

  if (isMobile) {
    return (
      <>
        {editor}
        <UnpublishedWebsiteWarning {...unpublishedWebsiteWarningProps} />
      </>
    );
  }

  const routes: Array<RoutePath<{}>> = [
    {
      path: routeHelpers.buildPath(postOfficeRoutePaths.create.path),
      component: () => editor,
    },
    {
      path: routeHelpers.buildPath(postOfficeRoutePaths.confirm.path),
      component: () => (
        <Confirm
          hideGuestSelector={hideGuestSelector}
          eventHandle={eventHandle}
          handleSendEmail={handleSendEmail}
          testEmail={testEmail}
          draftType={draftType}
          emailIsVerified={emailIsVerified}
        />
      ),
    },
  ];
  return (
    <>
      <Switch>
        {routes.map(route => (
          <Route
            key={route.path}
            path={route.path}
            render={route.component}
          />
        ))}
        <Redirect to={routes[0].path} />
      </Switch>
      <UnpublishedWebsiteWarning {...unpublishedWebsiteWarningProps} />
    </>
  );
};

export const RouterPostOffice: React.FC<{ eventId: string; eventHandle: string; eventDisplayName: string }> = ({ eventId, eventHandle, eventDisplayName }) => {
  const { draftType, fromDashboard, MIN_HEIGHT, MAX_HEIGHT, views, isThankYouMessageLoading, setIsThankYouMessageLoading } = usePostOfficeRouterController();
  const useProMessaging = useProFeature(EventFeature.messaging);
  const location = useLocation();

  // Redirect weddings and general events to the new general message route when creating generic messages
  if (useProMessaging && draftType === 'generic') {
    return (
      <Redirect
        to={{
          pathname: `/${eventHandle}/edit/message/general/create`,
          search: location.search,
        }}
      />
    );
  }

  return (
    <JoyKitThemeProvider theme={JoyKitV2AdminTheme}>
      <RoutesProvider
        appUrl={'email'}
        handle={eventHandle}
      >
        <MustBeSignedIn>
          <PeopleProvider>
            <TelemetryProvider context={{ eventId }}>
              <PostOffice
                eventHandle={eventHandle}
                draftType={draftType}
                fromDashboard={fromDashboard}
                isThankYouMessageLoading={isThankYouMessageLoading}
              >
                <PostOfficeBottomSheetProvider
                  views={views}
                  minHeight={MIN_HEIGHT}
                  maxHeight={MAX_HEIGHT}
                  initialParentViewState={{
                    eventHandle,
                    isConfirmSheetOpen: false,
                  }}
                >
                  <RoutesPostOffice
                    draftType={draftType}
                    eventDisplayName={eventDisplayName}
                    eventHandle={eventHandle}
                    eventId={eventId}
                    setIsThankYouMessageLoading={setIsThankYouMessageLoading}
                  />
                  <PostOfficeBottomSheet disableNavigation={draftType === 'thankyou' && isThankYouMessageLoading} />
                  <MobileSendTestEmailDialog eventHandle={eventHandle} />
                  {draftType === 'thankyou' ? <ConfirmSheetThankYou /> : <ConfirmSheet />}
                </PostOfficeBottomSheetProvider>
              </PostOffice>
            </TelemetryProvider>
          </PeopleProvider>
        </MustBeSignedIn>
      </RoutesProvider>
    </JoyKitThemeProvider>
  );
};
